export interface SpineSelectionItem {
  id: string;
  playIndex: number;
  layerIconId?: string;
  gid?: string;
  background?: number;
  name?: string;
  skuIds?: string[];
  hidden?: boolean;
  /**
   * 与普通模版 layer.opt 一致：
   * true = 可选（非必选）；false = 必选。
   * undefined 视为未设置（UI 当可选）。
   */
  opt?: boolean;
}

/** 按 playIndex 排序，保证保存/展示顺序一致 */
export function sortSelectionsByPlayIndex<T extends { playIndex?: number }>(
  selections: T[],
): T[] {
  return [...selections].sort(
    (a, b) => (a.playIndex ?? 0) - (b.playIndex ?? 0),
  );
}

/**
 * 从 Selection 内 SKU 推导可写入 requireCategories 的 category 列表。
 * 排除 static（静态层 SKU 不参与随机必选）。
 */
export function getSelectionRequireCategories(
  selection: { skuIds?: string[] } | null | undefined,
  skus: Record<string, { category?: string }> | null | undefined,
): string[] {
  if (!selection?.skuIds?.length || !skus) return [];
  const set = new Set<string>();
  for (const skuId of selection.skuIds) {
    const category = skus[skuId]?.category;
    if (category && category !== "static") {
      set.add(category);
    }
  }
  return Array.from(set);
}

/** 层是否必选：以 selection.opt 为准（false = 必选） */
export function isSelectionLayerRequired(
  selection: { opt?: boolean } | null | undefined,
): boolean {
  return selection?.opt === false;
}

/**
 * 按 required 开关，把 Selection 对应的 categories 并入 / 移出 requireCategories。
 */
export function syncRequireCategoriesForSelection(
  requireCategories: string[] | null | undefined,
  categories: string[],
  required: boolean,
): string[] {
  if (categories.length === 0) return [...(requireCategories || [])];
  const set = new Set(requireCategories || []);
  if (required) {
    for (const c of categories) set.add(c);
  } else {
    for (const c of categories) set.delete(c);
  }
  return Array.from(set);
}

/** LayerIcon.opt：true=可选，falsy=必选（与普通模版一致） */
export function isLayerIconRequired(
  icon: { opt?: boolean } | null | undefined,
): boolean {
  return !icon?.opt;
}

/** 从 LayerIcon.opt 得到 selection.opt（true=可选，false=必选） */
export function selectionOptFromLayerIcon(
  icon: { opt?: boolean } | null | undefined,
): boolean {
  return icon?.opt ? true : false;
}

/**
 * Skin 分类 ↔ 层联动：按 category 是否必选，回写包含该 category 的 Selection.opt。
 * 假定一类对应一层。
 */
export function syncSelectionOptForCategory(
  selections: any[],
  skus: Record<string, { category?: string }> | null | undefined,
  category: string,
  categoryRequired: boolean,
): any[] {
  return selections.map((sel) => {
    const cats = getSelectionRequireCategories(sel, skus);
    if (!cats.includes(category)) return sel;
    return {
      ...sel,
      // category 必选 → 层 opt=false；取消必选 → 层 opt=true
      opt: categoryRequired ? false : true,
    };
  });
}

/** 从 spineTemplates 的 selections + selectionIds 构建 logicalId -> docId 映射 */
export function buildExistingSelectionDocIdMap(
  template: { selections?: string; selectionIds?: string } | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!template?.selectionIds || !template?.selections) return map;

  try {
    const selectionIds = JSON.parse(template.selectionIds) as string[];
    const savedSelections = JSON.parse(template.selections) as Array<{
      id?: string;
    }>;
    savedSelections.forEach((sel, index) => {
      if (sel?.id && selectionIds[index]) {
        map.set(sel.id, selectionIds[index]);
      }
    });
  } catch (e) {
    console.warn("解析已有 selectionIds/selections 失败:", e);
  }

  return map;
}

export function parseSelectionFromDbItem(item: {
  data: string;
}): SpineSelectionItem {
  const sel = JSON.parse(item.data);
  return {
    id: sel.id,
    playIndex: sel.playIndex,
    layerIconId: sel.layerIconId,
    gid: sel.gid,
    background: sel.background,
    name: sel.name,
    skuIds: sel.skuIds || [],
    hidden: sel.hidden || undefined,
    opt: typeof sel.opt === "boolean" ? sel.opt : undefined,
  };
}

/** 按 selectionIds 顺序还原 selections，并保证 playIndex 排序 */
export function orderSelectionsFromDb(
  selectionIds: string[],
  selectionsFromDb: Array<{ id: string; data: string }>,
): SpineSelectionItem[] {
  const byDocId = new Map(selectionsFromDb.map((item) => [item.id, item]));

  const ordered = selectionIds
    .map((docId) => byDocId.get(docId))
    .filter((item): item is { id: string; data: string } => Boolean(item))
    .map(parseSelectionFromDbItem);

  return sortSelectionsByPlayIndex(ordered);
}
